import {
  BulkAddCommercialsBody,
  BulkAddCommercialsResult,
  BulkCreateCommercialAccountsBody,
  BulkCreateCommercialAccountsResult,
  CommercialMeeting,
  CommercialType,
  CreateCommercialAccountBody,
  ExponentStructureType,
  StructureTypeFormatted,
  UpdateCommercialAccountBody,
  Visitor,
} from "@ac-expo/api-types";
import { handleApiCall } from "../api-error-handler";
import { CommercialRoles } from "@prisma/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { eventsExhibitorsQueryKeys, eventsQueryKeys } from "../query-keys";
import { organiserExhibitorsQueryKeys } from "../query-keys/organiser.query-keys";
import structureQueryKeys from "../query-keys/structure.query-keys";
import userQueryKeys from "../query-keys/user.query-keys";
import {
  CommercialExhibitorMeetingForm,
  CommercialExhibitorMeetingUpdateForm,
  CommercialExhibitorUnavailabilityForm,
  PartialAddVisitorFromDataForm,
  UpdateCommercialStructureForm,
  UpdateCustomStructureAppearanceForm,
  UpdateStructureForm,
} from "../schemas";

type ImportVisitorBody = {
  import: PartialAddVisitorFromDataForm[];
  deletedIds: string[];
};

const updateStructure = async (id: string, body: UpdateStructureForm) => {
  try {
    const { data } = await privateAxios.patch(`/structures/${id}`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useUpdateStructureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStructureForm }) =>
      updateStructure(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.all,
      });
    },
  });
};

const updateStructureDirectoryVisibility = (id: string, hidden: boolean) =>
  handleApiCall(() =>
    privateAxios.patch(`/structures/${id}/directory-visibility`, { hidden })
  );

export const useUpdateStructureDirectoryVisibilityMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) =>
      updateStructureDirectoryVisibility(id, hidden),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.structure(),
      });
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.me(),
      });
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.lists(),
      });
    },
  });
};

const updateCustomStructure = async (id: string, body: UpdateCustomStructureAppearanceForm) => {
  try {
    const { data } = await privateAxios.patch(`/structures/${id}/custom`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useUpdateCustomStructureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomStructureAppearanceForm }) =>
      updateCustomStructure(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.all,
      });
    },
  });
};

async function requestParticipation(eventId: string) {
  try {
    const { data } = await privateAxios.post(`/structures/request`, {
      eventId,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) error.message = error.response?.data?.message ?? error.message;
    throw error;
  }
}

export const useRequestParticipationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) => requestParticipation(eventId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsExhibitorsQueryKeys.list(eventId),
      });
    },
  });
};

async function deleteRequest(id: string) {
  try {
    await privateAxios.delete(`/structures/delete-request/${id}`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
}

export function useDeleteRequestMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.all,
      });
    },
  });
}

async function likeStructure(structureId: string, like: boolean) {
  try {
    await privateAxios.post(`/structures/like`, {
      structureId,
      like,
    });
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
}

export function useLikeStructureMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ structureId, like }: { structureId: string; like: boolean }) =>
      likeStructure(structureId, like),
    onSuccess: (_data, { structureId, like }) => {
      queryClient.setQueryData(
        organiserExhibitorsQueryKeys.get(structureId),
        (old: StructureTypeFormatted) => ({
          ...old,
          isLiked: like,
        }),
      );
      queryClient.setQueryData(
        organiserExhibitorsQueryKeys.getAsOrganiser(structureId),
        (old: StructureTypeFormatted) => ({
          ...old,
          isLiked: like,
        }),
      );
      queryClient.setQueriesData(
        { queryKey: structureQueryKeys.exponentView(structureId) },
        (old: ExponentStructureType) => ({
          ...old,
          isLiked: like,
        }),
      );
    },
  });
}

type AddCommercialBody = ({ connectId: string } | { mail: string }) & {
  jobTitle: string;
  commercialRole: CommercialRoles;
};

export const addCommercial = async (
  body: AddCommercialBody,
  structureId: string,
  eventId?: string,
) => {
  const url = eventId
    ? `/structures/${structureId}/commercial/event/${eventId}`
    : `/structures/${structureId}/commercial`;
  const { data } = await privateAxios.post(url, body);
  return data;
};

export const addCommercialToEvent = async (
  body: AddCommercialBody,
  structureId: string,
  eventId: string,
) => {
  const { data } = await privateAxios.post(
    `/structures/${structureId}/commercial/event/${eventId}`,
    body,
  );
  return data;
};

export const updateCommercialStructure = async (
  body: UpdateCommercialStructureForm,
  structureId: string,
  commercialId: string,
) => {
  const { data } = await privateAxios.patch(
    `/structures/${structureId}/commercial/${commercialId}`,
    body,
  );
  return data;
};

export const useUpdateCommercialStructure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      body,
      structureId,
      commercialId,
    }: {
      body: UpdateCommercialStructureForm;
      structureId: string;
      commercialId: string;
      eventId: string;
    }) => updateCommercialStructure(body, structureId, commercialId),
    onSuccess: (data, variables) => {
      const { structureId, eventId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercialBase(structureId, eventId),
      });
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.structure(),
      });
    },
  });
};

type CreateCommercial = {
  firstName: string;
  lastName: string;
  email: string;
};

export const createCommercials = async (body: CreateCommercial[], structureId: string) => {
  try {
    const { data } = await privateAxios.post(`/structures/${structureId}/commercial/many`, {
      commercials: body,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useCreateCommercialsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body, structureId }: { body: CreateCommercial[]; structureId: string }) =>
      createCommercials(body, structureId),
    onSuccess: (data, variables) => {
      const { structureId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercial(structureId),
      });
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.structure(),
      });
    },
  });
};

type UpdateCommercial = {
  id: string;
  data: CreateCommercial;
};

export const updateCommercials = async (body: UpdateCommercial[], structureId: string) => {
  try {
    const { data } = await privateAxios.patch(`/structures/${structureId}/commercial`, {
      commercials: body,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message);
    }
    throw error;
  }
};

export const useUpdateCommercialsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body, structureId }: { body: UpdateCommercial[]; structureId: string }) =>
      updateCommercials(body, structureId),
    onSuccess: (data, variables) => {
      const { structureId, body } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercial(structureId),
      });
      body.forEach(({ id }) => {
        queryClient.invalidateQueries({
          queryKey: structureQueryKeys.getByCommercialId(id),
        });
      });
    },
  });
};

const deleteCommercialFromStructure = async (commercialId: string, structureId: string) => {
  return privateAxios.delete(`/structures/${structureId}/commercial/${commercialId}`);
};

type DeleteCommercialFromStructureBody = {
  commercialId: string;
  structureId: string;
};

export const useDeleteCommercialFromStructureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commercialId, structureId }: DeleteCommercialFromStructureBody) =>
      deleteCommercialFromStructure(commercialId, structureId),
    onSuccess: (data, variables) => {
      const { structureId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercial(structureId),
      });
    },
  });
};

export const useAddCommercialToEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      body,
      structureId,
      eventId,
    }: {
      body: AddCommercialBody;
      structureId: string;
      eventId: string;
    }) => addCommercialToEvent(body, structureId, eventId),
    onSuccess: (data, variables) => {
      const { structureId, eventId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercialBase(structureId, eventId),
      });
    },
  });
};

const bulkAddCommercialsToEvent = async (
  structureId: string,
  eventId: string,
  body: BulkAddCommercialsBody
): Promise<BulkAddCommercialsResult> => {
  const response = await privateAxios.post(
    `/structures/${structureId}/commercial/event/${eventId}/bulk`,
    body
  );
  return response.data;
};

export const useBulkAddCommercialsToEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      structureId,
      eventId,
      body,
    }: {
      structureId: string;
      eventId: string;
      body: BulkAddCommercialsBody;
    }) => bulkAddCommercialsToEvent(structureId, eventId, body),
    onSuccess: (data, variables) => {
      const { structureId, eventId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercialBase(structureId, eventId),
      });
    },
  });
};

const createCommercialAccount = (
  structureId: string,
  body: CreateCommercialAccountBody,
  eventId?: string,
) => {
  const url = eventId
    ? `/structures/${structureId}/commercial/event/${eventId}/account`
    : `/structures/${structureId}/commercial/account`;
  return handleApiCall(() => privateAxios.post<CommercialType>(url, body));
};

export const useCreateCommercialAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      structureId,
      eventId,
      body,
    }: {
      structureId: string;
      eventId?: string;
      body: CreateCommercialAccountBody;
    }) => createCommercialAccount(structureId, body, eventId),
    onSuccess: (_data, { structureId, eventId }) => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercialBase(structureId, eventId ?? ""),
      });
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercial(structureId),
      });
      queryClient.invalidateQueries({ queryKey: structureQueryKeys.all });
    },
  });
};

const updateCommercialAccount = (
  structureId: string,
  commercialId: string,
  body: UpdateCommercialAccountBody,
) =>
  handleApiCall(() =>
    privateAxios.patch<CommercialType>(
      `/structures/${structureId}/commercial/${commercialId}/account`,
      body,
    ),
  );

export const useUpdateCommercialAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      structureId,
      commercialId,
      body,
    }: {
      structureId: string;
      commercialId: string;
      body: UpdateCommercialAccountBody;
    }) => updateCommercialAccount(structureId, commercialId, body),
    onSuccess: (_data, { structureId }) => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercial(structureId),
      });
      queryClient.invalidateQueries({ queryKey: structureQueryKeys.all });
    },
  });
};

const bulkCreateCommercialAccounts = (
  structureId: string,
  body: BulkCreateCommercialAccountsBody,
) =>
  handleApiCall(() =>
    privateAxios.post<BulkCreateCommercialAccountsResult>(
      `/structures/${structureId}/commercial/account/bulk`,
      body,
    ),
  );

export const useBulkCreateCommercialAccountsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      structureId,
      body,
    }: {
      structureId: string;
      body: BulkCreateCommercialAccountsBody;
    }) => bulkCreateCommercialAccounts(structureId, body),
    onSuccess: (_data, { structureId }) => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercial(structureId),
      });
      queryClient.invalidateQueries({ queryKey: structureQueryKeys.all });
    },
  });
};

export const useAddCommercialMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body, structureId }: { body: AddCommercialBody; structureId: string }) =>
      addCommercial(body, structureId),
    onSuccess: (data, variables) => {
      const { structureId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercial(structureId),
      });
      queryClient.invalidateQueries({ queryKey: structureQueryKeys.all });
    },
  });
};

export const deleteCommercialFromEvent = async (
  commercialId: string,
  eventId: string,
  structureId: string,
) => {
  await privateAxios.delete(
    `/structures/${structureId}/commercial/event/${eventId}/${commercialId}`,
  );
};

export const useDeleteCommercialFromEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commercialId,
      eventId,
      structureId,
    }: {
      commercialId: string;
      eventId: string;
      structureId: string;
    }) => deleteCommercialFromEvent(commercialId, eventId, structureId),
    onSuccess: (data, variables) => {
      const { structureId, eventId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercialBase(structureId, eventId),
      });
    },
  });
};

export const addExhibitorVisitor = async (
  structureId: string,
  body: { connectId: string } | { data: PartialAddVisitorFromDataForm },
) => {
  try {
    const { data } = await privateAxios.post<Visitor>(
      "/structures/" + structureId + "/visitors",
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useAddExhibitorVisitorMutation = (structureId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { connectId: string } | { data: PartialAddVisitorFromDataForm }) =>
      addExhibitorVisitor(structureId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.visitors(structureId),
      });
    },
  });
};

export const importExhibitorVisitors = async (structureId: string, body: ImportVisitorBody) => {
  const { data } = await privateAxios.post("/structures/" + structureId + "/visitors/import", body);
  return data;
};

export const useImportExhibitorVisitorsMutation = (structureId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ImportVisitorBody) => importExhibitorVisitors(structureId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.visitors(structureId),
      });
    },
  });
};

export const assignCommercialsToEvent = async (
  structureId: string,
  eventId: string,
  commercialIds: string[],
) => {
  const { data } = await privateAxios.post(
    `/structures/${structureId}/commercial/event/${eventId}/assign`,
    commercialIds,
  );
  return data;
};

export const useAssignCommercialsToEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      structureId,
      eventId,
      commercialIds,
    }: {
      structureId: string;
      eventId: string;
      commercialIds: string[];
    }) => assignCommercialsToEvent(structureId, eventId, commercialIds),
    onSuccess: (data, variables) => {
      const { structureId, eventId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercialBase(structureId, eventId),
      });
    },
  });
};

const getStructureQrCode = async (structureId: string, eventId: string) => {
  const { data } = await privateAxios.get<Buffer>(`/exhibitor/qr-code/${eventId}/${structureId}`, {
    responseType: "arraybuffer",
  });
  return data;
};

export const useGetStructureQrCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ structureId, eventId }: { structureId: string; eventId: string }) =>
      getStructureQrCode(structureId, eventId),
    onSuccess: (data, variables) => {
      const { structureId, eventId } = variables;
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.qrCode(structureId, eventId),
      });
    },
  });
};

async function updateCommercialExhibitorMeeting(
  id: string,
  body: CommercialExhibitorMeetingUpdateForm,
) {
  const res = await privateAxios.patch<CommercialMeeting>(`/structures/meeting/${id}`, body);
  return res.data;
}

export function useUpdateCommercialExhibitorMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CommercialExhibitorMeetingUpdateForm }) =>
      updateCommercialExhibitorMeeting(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.meetings,
      });
    },
  });
}

async function deleteCommercialExhibitorMeeting(id: string) {
  const res = await privateAxios.delete<CommercialMeeting>(`/structures/meeting/${id}`);
  return res.data;
}

export function useDeleteCommercialExhibitorMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteCommercialExhibitorMeeting(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.meetings,
      });
    },
  });
}

async function createCommercialExhibitorMeeting(body: CommercialExhibitorMeetingForm) {
  try {
    const res = await privateAxios.post<CommercialMeeting>(`/structures/meeting`, body);
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { ...error, message: error.response?.data.message };
    }
    throw error;
  }
}

export function useCreateCommercialExhibitorMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: CommercialExhibitorMeetingForm }) =>
      createCommercialExhibitorMeeting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.meetings,
      });
    },
  });
}

async function setCommercialExhibitorUnavailabilities(body: CommercialExhibitorUnavailabilityForm) {
  const { commercialId, unavailabilities } = body;
  const res = await privateAxios.post<CommercialMeeting>(
    `/structures/meeting/availability/${commercialId}`,
    { unavailabilities },
  );
  return res.data;
}

export function useSetCommercialExhibitorUnavailabilities(structureId: string, eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: CommercialExhibitorUnavailabilityForm }) =>
      setCommercialExhibitorUnavailabilities(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.commercialBase(structureId, eventId),
      });
    },
  });
}
